import { waterfall } from '$lib/utils/objects';
import { EventType } from './common';
import { Sentry } from '$lib/services/sentry';

/**
 * @type {import('./types').Transports.AdjustServerSideTransport}
 */
export class AdjustServerSideTransport {
	/**
	 * @param {string} name - name of the transport
	 * @param {{ [K in EventType]: Array<Function>; }} tranformMap the map of transformation
	 * @param {typeof import('$lib/services/api').ApiService} apiService ApiService for server-side API calls
	 */
	constructor(name, tranformMap, apiService) {
		this.name = name;
		this.transformMap = tranformMap;
		this.apiService = apiService;
	}

	/**
	 * @param {import("./types").Events.Event<EventType, object>} event - the event to consume
	 * @returns {Promise<boolean>}
	 */
	async consume(event) {
		if (event) {
			const eventType = event.type;
			let transformedEvent = event;
			if (this.transformMap[eventType]) {
				transformedEvent = await waterfall(this.transformMap[eventType], event);
			}
			await this.emit(transformedEvent);

			return true;
		}
		return false;
	}

	/**
	 * Sends transformed events to the API. Events should already be in the final API payload format.
	 * @param {Array<object> | object} events - event(s) to send (already transformed to API payload format)
	 * @returns {Promise<boolean>}
	 */
	async emit(events) {
		const eventsArray = Array.isArray(events) ? events : [events];
		/** @type {Array<boolean>} */
		const results = [];
		
		for (const event of eventsArray) {
			try {
				// Skip if no event data (e.g., non-iOS platform returned empty array)
				if (!event || !event.event_type || !event.event_info) {
					continue;
				}

				// Event is already in API payload format from transformer
				await this.apiService.logThirdPartyConversion(event);
				results.push(true);
			} catch (e) {
				results.push(false);
				this.logException(e, event);
			}
		}

		return !results.includes(false);
	}

	/**
	 * @param {object | unknown} exception - the error being thrown
	 * @param {object} event - the event for which error occurs
	 * @returns {void}
	 */
	logException(exception, event) {
		Sentry.captureException(exception, {
			extra: {
				source: 'Error: Adjust Server-Side Transport',
				event
			}
		});
	}
}

